import http from '../utils/http'

var PORTAL_BASE = '/api/portal'

// 不做客户端缓存：portal 数据（尤其是"我的常玩"）必须与后端强一致，
// 客户端持久缓存会导致跨会话/跨账号污染。仅保留 inflight 用于同一渲染周期内的请求去重。
var inflight = {}

// 一次性清理旧版本残留的持久缓存
try { localStorage.removeItem('gc_portal_cache') } catch (e) {}

function stripHost(url) {
  if (!url) return url
  // 剥离任何协议 + host（http/https + 域名/IP + 可选端口），只保留路径
  // 兼容后端返回的绝对 URL（无论 http://mygames.gpcard.cn/xxx、https://.../xxx、http://122.51.97.165:8080/xxx）
  var path = url
  var m = /^https?:\/\/[^/]+(\/.*)?$/i.exec(url)
  if (m) path = m[1] || '/'
  // 统一补齐 /mygames 前缀（后端所有资源都部署在 /mygames 子路径下）
  // - 已带 /mygames：保持不动
  // - 未带前缀（如 /uploads/xxx.webp）：自动补上
  // - 非 / 开头的相对路径：不处理，交给浏览器自行解析
  if (path.charAt(0) === '/' && path.indexOf('/mygames') !== 0) {
    path = '/mygames' + path
  }
  return path
}

function firstImageUrl(value) {
  if (!value) return ''
  if (/^data:image\//i.test(String(value))) return String(value).trim()
  return String(value).split(',')[0].trim()
}

function tagColorFor(game) {
  return game.backgroundColor || game.tagColor || game.color || '#2F8CFF'
}

function transformGame(g) {
  if (!g) return null
  var players = (g.virtualPlayers || 0)
  var playersText = players > 0 ? players + '万+人在玩' : '0人在玩'
  var icon = stripHost(firstImageUrl(g.gameIconPath || g.gameIcon || g.icon || g.logo || ''))
  var cover = stripHost(firstImageUrl(g.gameImageUrl || g.gamePicture || g.horizontalPicture || g.cover || g.gameImageBase64 || ''))
  return {
    id: String(g.id),
    name: g.gameName || g.name || '',
    icon: icon,
    cover: cover,
    category: g.categoryName || g.category || '',
    categoryCode: g.categoryCode || '',
    rating: g.rating || 8.0,
    players: playersText,
    size: g.size || '',
    desc: g.gameDescription || g.desc || g.subtitle || '',
    gameDescription: g.gameDescription || g.desc || g.subtitle || '',
    gameShortDescription: g.gameShortDescription || '',
    tags: g.tags || [],
    tagText: g.tagName || g.tagText || '',
    badge: g.badge || '',
    tagCode: g.tagCode || g.tag || '',
    tagColor: tagColorFor(g),
    backgroundColor: g.backgroundColor || '',
    cpGameUrl: g.gameUrl || g.cpGameUrl || '',
    isNew: (g.tagCode || g.tag) === 'new',
    isHot: (g.tagCode || g.tag) === 'hot' || (g.heat && g.heat > 80),
    isRecommend: g.isRecommend === 1 || g.isRecommend === true,
    isFrequent: g.isFrequent === 1 || g.isFrequent === true,
    hotScore: g.heat || g.hotScore || 50,
    createTime: g.createTime || '2025-01-01',
    version: g.version || '',
    sortOrder: g.sortOrder || 0
  }
}

function transformMod(mod) {
  if (!mod) return null
  return {
    id: mod.id,
    sort: mod.sort || 0,
    title: mod.title || '',
    gameId: mod.gameId,
    backgroundImage: stripHost(mod.backgroundImage || ''),
    status: mod.status,
    game: transformGame(mod.game),
    games: (mod.games || []).map(transformGame).filter(Boolean)
  }
}

function flattenModGames(mods) {
  var games = []
  ;(mods || []).forEach(function(mod) {
    ;(mod.games || []).forEach(function(game) {
      games.push(game)
    })
    if (mod.game) games.push(mod.game)
  })
  return games
}

function transformPortalData(raw) {
  var games = (raw.allGames || []).map(transformGame).filter(Boolean)
  var featuredMods = (raw.featuredMods || []).map(transformMod).filter(Boolean)
  var normalMods = (raw.normalMods || []).map(transformMod).filter(Boolean)
  var featuredModGames = flattenModGames(featuredMods)
  var normalModGames = flattenModGames(normalMods)
  var userFavoriteGames = (raw.userFavoriteGames || raw.frequentGames || []).map(transformGame).filter(Boolean)
  // 我的常玩：只展示用户真实收藏/玩过的游戏。未登录或无常玩时展示空，
  // 由 UI 层决定占位（避免用推荐游戏冒充常玩，造成"每次刷新都出现无关图标"）
  var frequentGames = userFavoriteGames
  var recommendGames = (raw.recommendGames || []).map(transformGame).filter(Boolean)
  var categoryRawGames = (raw.categoryGames || []).map(transformGame).filter(Boolean)

  // 聚合所有来源的游戏到 gamesById
  var gamesById = {}
  function addToMap(list) {
    list.forEach(function(g) { if (!gamesById[g.id]) gamesById[g.id] = g })
  }
  addToMap(games)
  addToMap(featuredModGames)
  addToMap(normalModGames)
  addToMap(frequentGames)
  addToMap(recommendGames)
  addToMap(categoryRawGames)

  // 没有 allGames 时用所有已收集的游戏
  if (games.length === 0) {
    Object.keys(gamesById).forEach(function(k) { games.push(gamesById[k]) })
  }

  var banners = (raw.banners || [])
    .filter(function(b) { return b.gameId || b.title })
    .map(function(b) {
      var icon = b.gameIconPath || ''
      var emoji = icon
      if (/^https?:\/\//.test(icon)) {
        var nonAscii = icon.replace(/^https?:\/\/[^/\n]*\/*/, '')
        emoji = nonAscii || '🎮'
      }
      var imgUrl = stripHost(b.imageUrl || b.imageBase64 || '')
      return {
        id: String(b.id),
        title: b.title || '',
        subtitle: b.subtitle || '',
        desc: b.subtitle || '',
        imageUrl: imgUrl,
        gameId: b.gameId ? String(b.gameId) : null,
        gameName: b.gameName || '',
        gameUrl: b.gameUrl || '',
        emoji: emoji || '🎮',
        sortOrder: b.sortOrder || 0
      }
    })
    .sort(function(a, b) { return (a.sortOrder || 0) - (b.sortOrder || 0) })

  var marquees = (raw.marquees || []).map(function(m) {
    return {
      id: m.id,
      content: m.content || '',
      tag: m.tag || '',
      tagName: m.tagName || '',
      gameId: m.gameId || null,
      sortOrder: m.sortOrder || 0
    }
  })

  var categories = (raw.categories || []).sort(function(a, b) { return (a.sortOrder || 0) - (b.sortOrder || 0) })
  var categoryGamesMap = {}
  categories.forEach(function(cat) {
    categoryGamesMap[cat.categoryCode] = []
  })
  games.forEach(function(g) {
    var c = g.categoryCode
    if (!categoryGamesMap[c]) categoryGamesMap[c] = []
    categoryGamesMap[c].push(g)
  })

  return {
    games: games,
    gamesById: gamesById,
    banners: banners,
    marquees: marquees,
    frequentGames: frequentGames,
    recommendGames: recommendGames,
    featuredMods: featuredMods,
    normalMods: normalMods,
    featuredModGames: featuredModGames,
    normalModGames: normalModGames,
    categories: categories,
    categoryGamesMap: categoryGamesMap
  }
}

export function fetchPortalHome(userId) {
  var url = PORTAL_BASE + '/modpage'
  if (userId) { url += '?userId=' + encodeURIComponent(userId) }
  // 去重：同一渲染周期内并发的相同请求只发一次（响应回来后立即释放）
  if (inflight[url]) return inflight[url]
  var p = http.get(url)
    .then(function(data) {
      // http 拦截器已解包，若后端返回带 code 包装会自动提取 data；否则原样透传
      // 在浏览器控制台执行 localStorage.setItem('gc_debug_json','1') 开启 / localStorage.removeItem('gc_debug_json') 关闭
      if (typeof localStorage !== 'undefined' && localStorage.getItem('gc_debug_json') === '1') {
        console.log('[Portal] raw JSON from', url, ':', JSON.parse(JSON.stringify(data)))
      }
      if (data && (data.allGames || data.banners || data.categories || data.featuredMods || data.normalMods)) {
        return transformPortalData(data)
      }
      throw new Error('Invalid portal response')
    })
    .then(function(r) { delete inflight[url]; return r })
    .catch(function(e) { delete inflight[url]; throw e })
  inflight[url] = p
  return p
}

// 不做客户端缓存：每次都从后端拉取最新 JSON
export function getCachedPortalData(userId) {
  return fetchPortalHome(userId)
}

export function fetchMobilePortalHome() {
  return http.get(PORTAL_BASE + '/mobilehome').then(function(data) {
    if (data && data.allGames) return transformPortalData(data)
    throw new Error('Invalid mobile portal response')
  })
}

export function fetchPortalGameById(id, userId) {
  return fetchPortalHome(userId).then(function(data) {
    return data.gamesById[String(id)] || null
  })
}

export function invalidateCache() {
  // 兼容旧调用：现已无缓存可清
  try { localStorage.removeItem('gc_portal_cache') } catch (e) {}
}
